import { create } from "zustand";
import * as api from "../lib/api";

interface AuthState {
  checked: boolean;
  setupComplete: boolean;
  authenticated: boolean;
  loading: boolean;
  error: string | null;
  totpRequired: boolean;
  checkAuth: () => Promise<void>;
  setup: (password: string) => Promise<void>;
  login: (password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  checked: false,
  setupComplete: false,
  authenticated: false,
  loading: false,
  error: null,
  totpRequired: false,

  checkAuth: async () => {
    try {
      const status = await api.getAuthStatus();
      set({ checked: true, setupComplete: status.setupComplete });
      if (status.setupComplete) {
        try {
          await api.fetchCalendars();
          set({ authenticated: true });
        } catch {
          set({ authenticated: false });
        }
      }
    } catch {
      set({ checked: true, error: "Failed to check auth status" });
    }
  },

  setup: async (password: string) => {
    set({ loading: true, error: null });
    try {
      await api.setup(password);
      set({ loading: false, setupComplete: true, authenticated: true });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Setup failed" });
    }
  },

  login: async (password: string, totp?: string) => {
    set({ loading: true, error: null, totpRequired: false });
    try {
      const result = await api.login(password, totp);
      if (result.totpRequired) {
        set({ loading: false, totpRequired: true });
      } else {
        set({ loading: false, authenticated: true });
      }
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : "Login failed" });
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } finally {
      set({ authenticated: false, totpRequired: false });
    }
  },

  clearError: () => set({ error: null }),
}));
